import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Modal, Checkbox, Select, DatePicker, Button, Space, Typography, Spin, Empty, message } from 'antd';
import { CopyOutlined, PrinterOutlined } from '@ant-design/icons';
import api from '../api';
import RecapMaterial, { recapMaterialText } from './RecapMaterial';

// Each object and the access flag (1 or more) it needs; the server checks the
// same. Agents get none of them.
export const RECAP_OBJECTS = [
  { key: 'evaluations', label: 'Evaluations', flag: 'CanEditEvals',
    desc: 'completed evaluations by call date. Questions with a note or lost points, and comments.' },
  { key: 'errors',      label: 'Errors',      flag: 'CanEditErrors',
    desc: 'completed error submissions by date of error. Questions marked as an error, feedback or praise, notes, and comments.' },
  { key: 'coaching',    label: 'Coaching',    flag: 'CanEditCoaching',
    desc: 'sessions created in the period (not canceled). The session note, the evaluations and errors it references, and comments.' },
];

// Nothing relevant was recorded before this, so the popup never starts
// earlier (it can still be picked).
const EARLIEST_DEFAULT = dayjs('2026-10-01');

export function recapObjectsFor(user) {
  if (!user || user.Role === 'agent') return [];
  return RECAP_OBJECTS.filter((o) => (user[o.flag] ?? 0) >= 1);
}

/**
 * Agent Summary > Generate Recap: what was written about the agent in their
 * evaluations, errors and coaching sessions, for the objects, campaign and
 * dates picked here (starting from the page's filters). Read-only.
 */
export default function AgentRecapModal({
  open, onClose, currentUser, agentUserId, agentName,
  campaignOptions, defaultCampaign, defaultRange,
}) {
  const objects = recapObjectsFor(currentUser);
  const [include, setInclude]   = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [range, setRange]       = useState(null);
  const [material, setMaterial] = useState(null);
  const [loading, setLoading]   = useState(false);
  const printRef = useRef(null);

  // Start from the page's filters each time it opens.
  useEffect(() => {
    if (!open) return;
    setInclude(objects.map((o) => o.key));
    setCampaign(defaultCampaign ?? null);
    const [s0, e0] = defaultRange || [];
    const start = s0 && s0.isBefore(EARLIEST_DEFAULT, 'day') ? EARLIEST_DEFAULT : s0;
    setRange(start && e0 ? [start, e0.isBefore(start, 'day') ? start : e0] : defaultRange);
    setMaterial(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = () => {
    setLoading(true);
    setMaterial(null);
    api.get('/api/agent-recap', {
      params: {
        user_id: agentUserId,
        start: range[0].format('YYYY-MM-DD'),
        end: range[1].format('YYYY-MM-DD'),
        include: include.join(','),
        ...(campaign ? { campaign_id: campaign } : {}),
      },
    })
      .then((r) => setMaterial(r.data))
      .catch((err) => message.error(err.response?.data?.error || 'Failed to generate the recap.'))
      .finally(() => setLoading(false));
  };

  const campaignLabel = campaignOptions.find((c) => c.value === campaign)?.label || 'All campaigns';
  const heading = range
    ? `${agentName || 'Agent'} · ${campaignLabel} · ${range[0].format('MMM D, YYYY')} – ${range[1].format('MMM D, YYYY')}`
    : '';

  const copy = () => {
    navigator.clipboard.writeText(recapMaterialText(material, heading))
      .then(() => message.success('Copied.'))
      .catch(() => message.error('Copy failed.'));
  };

  // Its own window, light and without the popup around it: the material as
  // rendered here, with the heading.
  const print = () => {
    const w = window.open('', '_blank');
    if (!w) { message.error('Allow pop-ups for this site to print.'); return; }
    const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(heading)}</title>
<style>
  body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; font-size: 13px; color: #262626; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 16px; }
  a { color: #1677ff; text-decoration: none; }
  .ant-tag { display: inline-block; border: 1px solid #d9d9d9; border-radius: 4px; padding: 0 6px; font-size: 12px; margin-right: 4px; }
  .ant-typography-secondary, .ant-typography.ant-typography-secondary { color: #8c8c8c; }
  * { background: transparent !important; }
  div[style*="border-top"] { break-inside: avoid; }
</style></head><body><h1>${esc(heading)}</h1>${printRef.current ? printRef.current.innerHTML : ''}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const empty = material && !['evaluations', 'errors', 'coaching'].some((k) => material[k]?.length);

  return (
    <Modal
      title={`Generate Recap${agentName ? ` — ${agentName}` : ''}`}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      width="min(900px, 95vw)"
      footer={[
        material && !empty && <Button key="print" icon={<PrinterOutlined />} onClick={print}>Print</Button>,
        material && !empty && <Button key="copy" icon={<CopyOutlined />} onClick={copy}>Copy</Button>,
        <Button key="close" onClick={onClose}>Close</Button>,
      ].filter(Boolean)}
      destroyOnClose
    >
      {/* Only the objects this user can include. */}
      <div style={{ fontSize: 13, color: '#595959', marginBottom: 16 }}>
        <div>Shows what was written about this agent in the selected period:</div>
        <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
          {objects.map((o) => <li key={o.key}><strong>{o.label}:</strong> {o.desc}</li>)}
        </ul>
        <div>
          System comments are left out. This is read-only: nothing is saved or sent to the agent.
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 16 }}>
        <Checkbox.Group
          value={include}
          onChange={setInclude}
          options={objects.map((o) => ({ label: o.label, value: o.key }))}
        />
        <Space size={4}>
          <Typography.Text type="secondary">Campaign:</Typography.Text>
          <Select allowClear placeholder="All campaigns" value={campaign} onChange={setCampaign}
            options={campaignOptions} style={{ minWidth: 160 }} />
        </Space>
        <Space size={4}>
          <Typography.Text type="secondary">Date range:</Typography.Text>
          <DatePicker.RangePicker value={range} onChange={setRange} allowClear={false} />
        </Space>
        <Button type="primary" onClick={generate} loading={loading} disabled={!include.length || !range}>
          Generate
        </Button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
      {empty && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing found for these filters." />}
      {material && !empty && (
        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
          <div ref={printRef}><RecapMaterial material={material} /></div>
        </div>
      )}
    </Modal>
  );
}
